import React, { useState, useEffect } from 'react';
import spatialOptimizer from '../../utils/spatialOptimizer';
import HistoryPerformanceTest from './HistoryPerformanceTest';

/**
 * Performance Monitor Component
 * Real-time monitoring of spatial optimization performance
 * Helps identify bottlenecks and tune parameters
 */
const PerformanceMonitor = ({ subscriptionCount, busCount }) => {
  const [metrics, setMetrics] = useState({});
  const [isMonitoring, setIsMonitoring] = useState(false);
  const [testResults, setTestResults] = useState(null);
  const [activeTab, setActiveTab] = useState('spatial');

  // Update metrics every 3 seconds
  useEffect(() => {
    const updateMetrics = () => {
      setMetrics(spatialOptimizer.getMetrics());
    };

    updateMetrics();
    const interval = setInterval(updateMetrics, 3000);
    
    return () => clearInterval(interval);
  }, []);

  // Auto-tune optimizer based on metrics
  const handleAutoTune = () => {
    const tuneResults = spatialOptimizer.autoTune();
    setMetrics(tuneResults);
  };

  // Clear all caches
  const handleClearCache = () => {
    spatialOptimizer.clearCache();
    setMetrics(spatialOptimizer.getMetrics());
  };

  // Performance stress test
  const runStressTest = async () => {
    setIsMonitoring(true);

    try {
      // Generate synthetic test data
      const testBuses = generateTestBuses(100);
      const testSubscriptions = generateTestSubscriptions(500);
      const testTowns = generateTestTowns(20);

      // Test original algorithm
      const originalStart = performance.now();
      const originalResults = testOriginalAlgorithm(testBuses, testSubscriptions, testTowns);
      const originalTime = performance.now() - originalStart;

      // Test optimized algorithm
      spatialOptimizer.clearCache(); // Reset for fair test
      const optimizedStart = performance.now();
      const optimizedResults = spatialOptimizer.optimizedFilterSubscribedBuses(testBuses, testSubscriptions, testTowns);
      const optimizedTime = performance.now() - optimizedStart;

      const improvement = ((originalTime - optimizedTime) / originalTime * 100).toFixed(1);
      const speedup = (originalTime / optimizedTime).toFixed(1);

      setTestResults({
        originalTime: originalTime.toFixed(1),
        optimizedTime: optimizedTime.toFixed(1),
        improvement: improvement,
        speedup: speedup,
        originalCount: originalResults.length,
        optimizedCount: optimizedResults.length,
        accuracy: ((optimizedResults.length / originalResults.length) * 100).toFixed(1)
      });

    } catch (error) {
    } finally {
      setIsMonitoring(false);
    }
  };

  // Generate synthetic test data
  const generateTestBuses = (count) => {
    const buses = [];
    for (let i = 0; i < count; i++) {
      buses.push({
        id: `bus_${i}`,
        vehicle_type: 'bus',
        make: 'TestBus',
        model: 'Model',
        year: 2020,
        last_location: {
          latitude: 17.0 + (Math.random() - 0.5) * 2, // Belize area
          longitude: -88.0 + (Math.random() - 0.5) * 2
        }
      });
    }
    return buses;
  };

  const generateTestSubscriptions = (count) => {
    const subscriptions = [];
    for (let i = 0; i < count; i++) {
      subscriptions.push({
        id: `sub_${i}`,
        is_active: Math.random() > 0.2, // 80% active
        origin_town_id: `town_${Math.floor(Math.random() * 10)}`,
        destination_town_id: `town_${Math.floor(Math.random() * 10) + 10}`,
        pickup_location: {
          lat: 17.0 + (Math.random() - 0.5) * 2,
          lng: -88.0 + (Math.random() - 0.5) * 2
        },
        time_windows: [{
          is_enabled: true,
          start_time: '06:00',
          end_time: '22:00'
        }],
        days_of_week: {
          monday: true,
          tuesday: true,
          wednesday: true,
          thursday: true,
          friday: true,
          saturday: true,
          sunday: true
        }
      });
    }
    return subscriptions;
  };

  const generateTestTowns = (count) => {
    const towns = [];
    for (let i = 0; i < count; i++) {
      towns.push({
        id: `town_${i}`,
        name: `Town ${i}`,
        sequence_number: i,
        coordinates: {
          lat: 17.0 + (Math.random() - 0.5) * 2,
          lng: -88.0 + (Math.random() - 0.5) * 2
        }
      });
    }
    return towns;
  };

  // Simulate original algorithm for comparison
  const testOriginalAlgorithm = (buses, subscriptions, towns) => {
    const results = [];
    
    buses.forEach(bus => {
      if (!bus.last_location || bus.vehicle_type !== 'bus') return;
      
      subscriptions.forEach(subscription => {
        if (!subscription.is_active) return;
        
        const busLocation = {
          lat: bus.last_location.latitude,
          lng: bus.last_location.longitude
        };
        
        // Simple distance calculation (Haversine)
        const R = 3959;
        const dLat = (subscription.pickup_location.lat - busLocation.lat) * Math.PI / 180;
        const dLng = (subscription.pickup_location.lng - busLocation.lng) * Math.PI / 180;
        const a = Math.sin(dLat/2) * Math.sin(dLat/2) +
          Math.cos(busLocation.lat * Math.PI / 180) * Math.cos(subscription.pickup_location.lat * Math.PI / 180) * 
          Math.sin(dLng/2) * Math.sin(dLng/2);
        const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
        const distance = R * c;
        
        if (distance <= 30) {
          results.push({ bus, subscription, distance });
        }
      });
    });
    
    return results;
  };

  const getPerformanceColor = (value, type) => {
    if (type === 'time') {
      return parseFloat(value) < 50 ? 'text-green-600' : 
             parseFloat(value) < 200 ? 'text-yellow-600' : 'text-red-600';
    }
    if (type === 'hitrate') {
      return parseFloat(value) > 80 ? 'text-green-600' : 
             parseFloat(value) > 60 ? 'text-yellow-600' : 'text-red-600';
    }
    return 'text-gray-600';
  };

  return (
    <div className="bg-white rounded-lg shadow-lg p-6">
      <div className="flex justify-between items-center mb-6">
        <h3 className="text-xl font-bold text-gray-900">📊 Performance Monitor</h3>
        
        {/* Tab Navigation */}
        <div className="flex bg-gray-100 rounded-lg">
          <button
            onClick={() => setActiveTab('spatial')}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              activeTab === 'spatial' 
                ? 'bg-blue-600 text-white' 
                : 'text-gray-600 hover:text-gray-800'
            }`}
          >
            Spatial Optimizer
          </button>
          <button
            onClick={() => setActiveTab('history')}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              activeTab === 'history' 
                ? 'bg-blue-600 text-white' 
                : 'text-gray-600 hover:text-gray-800'
            }`}
          >
            History Optimizer
          </button>
        </div>
        
        {/* Spatial Optimizer Controls */}
        {activeTab === 'spatial' && (
          <div className="space-x-2">
            <button 
              onClick={handleAutoTune}
              className="px-3 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
            >
              Auto-Tune
            </button>
            <button 
              onClick={handleClearCache}
              className="px-3 py-2 bg-gray-600 text-white rounded hover:bg-gray-700"
            >
              Clear Cache
            </button>
            <button 
              onClick={runStressTest}
              disabled={isMonitoring}
              className="px-3 py-2 bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50"
            >
              {isMonitoring ? 'Testing...' : 'Stress Test'}
            </button>
          </div>
        )}
      </div>

      {/* Tab Content */}
      {activeTab === 'spatial' && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        {/* Current Performance */}
        <div className="bg-blue-50 rounded-lg p-4">
          <h4 className="font-semibold text-blue-800 mb-2">⚡ Current Performance</h4>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span>Response Time:</span>
              <span className={`font-mono ${getPerformanceColor(metrics.averageResponseTime, 'time')}`}>
                {metrics.averageResponseTime?.toFixed(1) || '0'}ms
              </span>
            </div>
            <div className="flex justify-between">
              <span>Distance Calculations:</span>
              <span className="font-mono text-gray-700">{metrics.distanceCalculations || 0}</span>
            </div>
            <div className="flex justify-between">
              <span>Grid Lookups:</span>
              <span className="font-mono text-gray-700">{metrics.gridLookups || 0}</span>
            </div>
          </div>
        </div>

        {/* Cache Performance */}
        <div className="bg-green-50 rounded-lg p-4">
          <h4 className="font-semibold text-green-800 mb-2">💾 Cache Performance</h4>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span>Hit Rate:</span>
              <span className={`font-mono ${getPerformanceColor(metrics.cacheHitRate, 'hitrate')}`}>
                {metrics.cacheHitRate || '0%'}
              </span>
            </div>
            <div className="flex justify-between">
              <span>Cache Size:</span>
              <span className="font-mono text-gray-700">{metrics.cacheSize || 0}</span>
            </div>
            <div className="flex justify-between">
              <span>Hits/Misses:</span>
              <span className="font-mono text-gray-700">
                {metrics.cacheHits || 0}/{metrics.cacheMisses || 0}
              </span>
            </div>
          </div>
        </div>

        {/* Spatial Index */}
        <div className="bg-purple-50 rounded-lg p-4">
          <h4 className="font-semibold text-purple-800 mb-2">🗺️ Spatial Index</h4>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span>Grid Cells:</span>
              <span className="font-mono text-gray-700">{metrics.gridCells || 0}</span>
            </div>
            <div className="flex justify-between">
              <span>Indexed Subscriptions:</span>
              <span className="font-mono text-gray-700">{metrics.indexedSubscriptions || 0}</span>
            </div>
            <div className="flex justify-between">
              <span>Last Optimized:</span>
              <span className="font-mono text-xs text-gray-500">
                {metrics.lastOptimizedAt ? new Date(metrics.lastOptimizedAt).toLocaleTimeString() : 'Never'}
              </span>
            </div>
          </div>
        </div>

        {/* System Load */}
        <div className="bg-yellow-50 rounded-lg p-4">
          <h4 className="font-semibold text-yellow-800 mb-2">📈 System Load</h4>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span>Current Buses:</span>
              <span className="font-mono text-gray-700">{busCount || 0}</span>
            </div>
            <div className="flex justify-between">
              <span>Subscriptions:</span>
              <span className="font-mono text-gray-700">{subscriptionCount || 0}</span>
            </div>
            <div className="flex justify-between">
              <span>Load Factor:</span>
              <span className={`font-mono ${
                (subscriptionCount || 0) > 200 ? 'text-red-600' :
                (subscriptionCount || 0) > 100 ? 'text-yellow-600' : 'text-green-600'
              }`}>
                {subscriptionCount && busCount ? ((subscriptionCount * busCount) / 1000).toFixed(1) + 'K' : '0'}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Stress Test Results */}
      {testResults && (
        <div className="bg-gray-50 rounded-lg p-4 mb-4">
          <h4 className="font-semibold text-gray-800 mb-3">🧪 Stress Test Results</h4>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
            <div>
              <span className="text-gray-600">Original Algorithm:</span>
              <div className="font-mono text-red-600 font-bold">{testResults.originalTime}ms</div>
            </div>
            <div>
              <span className="text-gray-600">Optimized Algorithm:</span>
              <div className="font-mono text-green-600 font-bold">{testResults.optimizedTime}ms</div>
            </div>
            <div>
              <span className="text-gray-600">Performance Gain:</span>
              <div className="font-mono text-blue-600 font-bold">+{testResults.improvement}%</div>
            </div>
            <div>
              <span className="text-gray-600">Speedup Factor:</span>
              <div className="font-mono text-purple-600 font-bold">{testResults.speedup}x faster</div>
            </div>
          </div>
          <div className="mt-3 text-xs text-gray-500">
            Results: {testResults.optimizedCount} matches ({testResults.accuracy}% accuracy vs original)
          </div>
        </div>
      )}

      {/* Performance Recommendations */}
      <div className="bg-blue-50 rounded-lg p-4">
        <h4 className="font-semibold text-blue-800 mb-3">💡 Performance Recommendations</h4>
        <div className="space-y-2 text-sm">
          {parseFloat(metrics.cacheHitRate || '0') < 70 && (
            <div className="flex items-center text-yellow-700">
              <span className="mr-2">⚠️</span>
              <span>Low cache hit rate ({metrics.cacheHitRate}). Consider increasing cache size.</span>
            </div>
          )}
          {(metrics.averageResponseTime || 0) > 100 && (
            <div className="flex items-center text-red-700">
              <span className="mr-2">🚨</span>
              <span>High response time ({metrics.averageResponseTime}ms). Consider auto-tuning or increasing grid size.</span>
            </div>
          )}
          {(subscriptionCount || 0) > 500 && (
            <div className="flex items-center text-blue-700">
              <span className="mr-2">📊</span>
              <span>High subscription count ({subscriptionCount}). Spatial optimization is highly recommended.</span>
            </div>
          )}
          {(metrics.gridCells || 0) === 0 && (subscriptionCount || 0) > 0 && (
            <div className="flex items-center text-orange-700">
              <span className="mr-2">🔧</span>
              <span>Spatial index not built. Run a test to initialize the optimizer.</span>
            </div>
          )}
        </div>
      </div>
      </>
    )}

    {/* History Performance Test Tab */}
    {activeTab === 'history' && (
      <HistoryPerformanceTest />
    )}
    
    </div>
  );
};

export default PerformanceMonitor;